import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowUpload16Regular,
  Dismiss24Regular,
  Delete20Regular,
  Edit16Regular,
  Video20Regular,
} from "@fluentui/react-icons";
import { APP_LOCALE } from "../../../config/locale";
import { formatAttachmentSize } from "./WorkDiaryAttachment";
import {
  ApiError,
  deleteWorkPhoto,
  fetchWorkPhotoBlob,
  getWorkPhotoFileUrl,
  getWorkPhotos,
  updateWorkPhotoNote,
  uploadWorkPhoto,
  type WorkPhotoAuthorDto,
  type WorkPhotoDto,
  type WorkPhotoFilters,
} from "../../../workPhotos/workPhotosApi";

interface WorkPhotoArchiveTabProps {
  workId: number;
}

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

// Fetches the PRIVATE media blob (authenticated endpoint), turns it into an
// objectURL for previewing, and revokes it on cleanup so we never leak memory.
function useWorkPhotoObjectUrl(
  photoId: number,
  enabled: boolean,
): { objectUrl: string | null; isLoading: boolean; hasError: boolean } {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    let createdUrl: string | null = null;
    setObjectUrl(null);
    setHasError(false);
    setIsLoading(true);

    fetchWorkPhotoBlob(photoId)
      .then((blob) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setObjectUrl(createdUrl);
      })
      .catch(() => {
        if (!cancelled) setHasError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [photoId, enabled]);

  return { objectUrl, isLoading, hasError };
}

interface ImageThumbProps {
  photo: WorkPhotoDto;
  onOpen: (objectUrl: string) => void;
}

// Authenticated image thumbnail: fetch → objectURL → click opens the lightbox with
// the SAME objectURL (no second fetch).
function ImageThumb({ photo, onOpen }: ImageThumbProps) {
  const { objectUrl, hasError } = useWorkPhotoObjectUrl(photo.id, true);

  if (hasError) {
    return (
      <div className="flex items-center justify-center w-full aspect-square bg-muted text-secondary-text text-xs px-2 text-center">
        Não foi possível carregar a pré-visualização.
      </div>
    );
  }

  if (!objectUrl) {
    return <div className="block w-full aspect-square bg-muted animate-pulse" />;
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(objectUrl)}
      title={photo.fileName}
      className="block w-full aspect-square overflow-hidden bg-muted cursor-pointer"
    >
      <img
        src={objectUrl}
        alt={photo.fileName}
        className="w-full h-full object-cover"
      />
    </button>
  );
}

const distinctAuthors = (photos: WorkPhotoDto[]): WorkPhotoAuthorDto[] => {
  const byId = new Map<string, WorkPhotoAuthorDto>();
  for (const photo of photos) {
    const author = photo.author;
    if (author && author.id && !byId.has(author.id)) {
      byId.set(author.id, author);
    }
  }
  return Array.from(byId.values()).sort((a, b) =>
    a.name.localeCompare(b.name, APP_LOCALE),
  );
};

export function WorkPhotoArchiveTab({ workId }: WorkPhotoArchiveTabProps) {
  const [photos, setPhotos] = useState<WorkPhotoDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Filters ("pesquisa por data/autor").
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [authorId, setAuthorId] = useState("");

  // Author dropdown options accumulate across loads (union) so filtering never
  // removes an option, and newly uploaded authors keep showing up.
  const [authorOptions, setAuthorOptions] = useState<WorkPhotoAuthorDto[]>([]);

  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const filtersActive = fromDate !== "" || toDate !== "" || authorId !== "";

  const mergeAuthors = useCallback((loaded: WorkPhotoDto[]) => {
    const fresh = distinctAuthors(loaded);
    setAuthorOptions((prev) => {
      const byId = new Map<string, WorkPhotoAuthorDto>();
      for (const author of prev) {
        if (author.id) byId.set(author.id, author);
      }
      for (const author of fresh) {
        if (author.id) byId.set(author.id, author);
      }
      return Array.from(byId.values()).sort((a, b) =>
        a.name.localeCompare(b.name, APP_LOCALE),
      );
    });
  }, []);

  const loadPhotos = useCallback(
    async (filters: WorkPhotoFilters, silent = false) => {
      if (!silent) setIsLoading(true);
      setError(null);
      try {
        const vm = await getWorkPhotos(workId, filters);
        const loaded = vm.photos ?? [];
        setPhotos(loaded);
        mergeAuthors(loaded);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Não foi possível carregar o arquivo fotográfico.",
        );
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [workId, mergeAuthors],
  );

  useEffect(() => {
    loadPhotos({
      from: fromDate || undefined,
      to: toDate || undefined,
      author: authorId || undefined,
    });
  }, [loadPhotos, fromDate, toDate, authorId]);

  const currentFilters = useMemo<WorkPhotoFilters>(
    () => ({
      from: fromDate || undefined,
      to: toDate || undefined,
      author: authorId || undefined,
    }),
    [fromDate, toDate, authorId],
  );

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (files.length === 0) return;
    setError(null);
    try {
      for (let i = 0; i < files.length; i += 1) {
        setUploadProgress(`A carregar… (${i + 1}/${files.length})`);
        await uploadWorkPhoto(workId, files[i]);
      }
      await loadPhotos(currentFilters, true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Não foi possível carregar o ficheiro. Apenas imagens e vídeos são aceites.",
      );
    } finally {
      setUploadProgress(null);
    }
  };

  const clearFilters = () => {
    setFromDate("");
    setToDate("");
    setAuthorId("");
  };

  const startEditNote = (photo: WorkPhotoDto) => {
    setEditingNoteId(photo.id);
    setNoteDraft(photo.note ?? "");
    setError(null);
  };

  const cancelEditNote = () => {
    setEditingNoteId(null);
    setNoteDraft("");
  };

  const handleSaveNote = async (id: number) => {
    if (savingNote) return;
    setSavingNote(true);
    setError(null);
    try {
      const trimmed = noteDraft.trim();
      const updated = await updateWorkPhotoNote(id, trimmed === "" ? null : trimmed);
      setPhotos((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item)),
      );
      cancelEditNote();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Não foi possível guardar a nota.",
      );
    } finally {
      setSavingNote(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (deletingId !== null) return;
    setDeletingId(id);
    setError(null);
    try {
      await deleteWorkPhoto(id);
      setPhotos((prev) => prev.filter((item) => item.id !== id));
      setConfirmDeleteId(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setPhotos((prev) => prev.filter((item) => item.id !== id));
        setConfirmDeleteId(null);
      } else {
        setError(
          err instanceof Error
            ? err.message
            : "Não foi possível eliminar o ficheiro.",
        );
        loadPhotos(currentFilters, true);
      }
    } finally {
      setDeletingId(null);
    }
  };

  // Close the image lightbox with Escape.
  useEffect(() => {
    if (!lightboxUrl) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setLightboxUrl(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightboxUrl]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2
            className="text-foreground"
            style={{
              fontSize: "var(--text-section-title)",
              fontWeight: "var(--font-weight-semi-bold)",
            }}
          >
            Arquivo Fotográfico
          </h2>
          <p className="text-secondary-text text-sm">
            {photos.length} ficheiro(s)
          </p>
        </div>
        <label
          className="inline-flex items-center gap-2 px-4 py-2 min-h-[44px] bg-primary/10 text-primary cursor-pointer text-sm"
          style={{ borderRadius: "var(--radius-button)" }}
        >
          <ArrowUpload16Regular className="w-4 h-4 flex-shrink-0" />
          <span>Carregar fotos/vídeos</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            hidden
            disabled={uploadProgress !== null}
            onChange={handleFileChange}
          />
        </label>
      </div>

      {uploadProgress && (
        <div className="text-secondary-text text-sm">{uploadProgress}</div>
      )}

      {error && (
        <div
          className="border border-border bg-destructive/10 text-destructive px-4 py-3"
          style={{ borderRadius: "var(--radius)" }}
        >
          {error}
        </div>
      )}

      {/* Filter bar: search by date / author */}
      <div
        className="bg-card border border-border p-4 flex flex-wrap items-end gap-3"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <label className="flex flex-col gap-1 text-sm text-secondary-text">
          De
          <input
            type="date"
            value={fromDate}
            max={toDate || undefined}
            onChange={(event) => setFromDate(event.target.value)}
            className="px-3 py-2 border border-border rounded bg-background text-foreground"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-secondary-text">
          Até
          <input
            type="date"
            value={toDate}
            min={fromDate || undefined}
            onChange={(event) => setToDate(event.target.value)}
            className="px-3 py-2 border border-border rounded bg-background text-foreground"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-secondary-text">
          Autor
          <select
            value={authorId}
            onChange={(event) => setAuthorId(event.target.value)}
            className="px-3 py-2 border border-border rounded bg-background text-foreground"
          >
            <option value="">Todos</option>
            {authorOptions.map((author) => (
              <option key={author.id ?? ""} value={author.id ?? ""}>
                {author.name}
              </option>
            ))}
          </select>
        </label>
        {filtersActive && (
          <button
            type="button"
            onClick={clearFilters}
            className="px-3 py-2 min-h-[44px] border border-border text-foreground hover:bg-accent/50 cursor-pointer text-sm"
            style={{ borderRadius: "var(--radius)" }}
          >
            Limpar filtros
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-secondary-text text-center py-6">A carregar…</div>
      ) : photos.length === 0 ? (
        <div
          className="bg-card border border-border text-secondary-text text-center px-4 py-8"
          style={{ borderRadius: "var(--radius-card)" }}
        >
          {filtersActive
            ? "Nenhum ficheiro corresponde aos filtros."
            : "Sem fotografias ou vídeos no arquivo desta obra."}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {photos.map((photo) => (
            <div
              key={photo.id}
              className="bg-card border border-border overflow-hidden flex flex-col"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {photo.isVideo ? (
                <video
                  controls
                  preload="metadata"
                  src={getWorkPhotoFileUrl(photo.id)}
                  className="w-full aspect-square object-cover bg-muted"
                />
              ) : photo.isImage ? (
                <ImageThumb photo={photo} onOpen={setLightboxUrl} />
              ) : (
                <div className="flex items-center justify-center w-full aspect-square bg-muted text-secondary-text text-xs px-2 text-center">
                  {photo.fileName}
                </div>
              )}

              <div className="p-3 space-y-2 flex flex-col">
                <div className="flex items-center gap-2">
                  {photo.isVideo && (
                    <Video20Regular className="w-4 h-4 text-primary flex-shrink-0" />
                  )}
                  <span
                    className="text-foreground text-sm truncate"
                    title={photo.fileName}
                  >
                    {photo.fileName}
                  </span>
                </div>
                <div className="text-secondary-text text-xs">
                  {formatDate(photo.created)} · {photo.author?.name || "—"}
                </div>
                <div className="text-secondary-text text-xs">
                  {formatAttachmentSize(photo.sizeInBytes)}
                </div>

                {/* Note (editable inline) */}
                {editingNoteId === photo.id ? (
                  <div className="space-y-2">
                    <textarea
                      rows={2}
                      value={noteDraft}
                      onChange={(event) => setNoteDraft(event.target.value)}
                      maxLength={2000}
                      placeholder="Nota…"
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground text-sm"
                    />
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        disabled={savingNote}
                        onClick={() => handleSaveNote(photo.id)}
                        className="px-2 py-1 text-xs bg-primary/10 text-primary cursor-pointer disabled:opacity-50"
                        style={{ borderRadius: "var(--radius)" }}
                      >
                        {savingNote ? "A guardar…" : "Guardar"}
                      </button>
                      <button
                        type="button"
                        disabled={savingNote}
                        onClick={cancelEditNote}
                        className="px-2 py-1 text-xs border border-border text-foreground hover:bg-accent/50 cursor-pointer disabled:opacity-50"
                        style={{ borderRadius: "var(--radius)" }}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : photo.note ? (
                  <button
                    type="button"
                    onClick={() => startEditNote(photo)}
                    title="Editar nota"
                    className="flex items-start gap-1 text-left text-foreground text-sm cursor-pointer"
                  >
                    <Edit16Regular className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                    <span className="break-words">{photo.note}</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => startEditNote(photo)}
                    className="inline-flex items-center gap-1 text-primary text-xs cursor-pointer w-fit"
                  >
                    <Edit16Regular className="w-4 h-4 flex-shrink-0" />
                    Adicionar nota
                  </button>
                )}

                {/* Delete (confirm-inline, 2 steps) */}
                <div className="flex items-center gap-2 flex-wrap">
                  {confirmDeleteId === photo.id ? (
                    <>
                      <span className="text-xs text-secondary-text">
                        Apagar?
                      </span>
                      <button
                        type="button"
                        disabled={deletingId === photo.id}
                        onClick={() => handleDelete(photo.id)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-destructive/10 text-destructive cursor-pointer disabled:opacity-50"
                        style={{ borderRadius: "var(--radius)" }}
                      >
                        {deletingId === photo.id ? "A eliminar…" : "Confirmar"}
                      </button>
                      <button
                        type="button"
                        disabled={deletingId === photo.id}
                        onClick={() => setConfirmDeleteId(null)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-border text-foreground hover:bg-accent/50 cursor-pointer disabled:opacity-50"
                        style={{ borderRadius: "var(--radius)" }}
                      >
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(photo.id)}
                      className="inline-flex items-center gap-1 text-destructive cursor-pointer text-sm"
                    >
                      <Delete20Regular className="w-4 h-4 flex-shrink-0" />
                      Eliminar
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {lightboxUrl && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Pré-visualização"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 cursor-pointer"
          style={{ backgroundColor: "rgba(0,0,0,0.8)" }}
          onClick={() => setLightboxUrl(null)}
        >
          <button
            type="button"
            onClick={() => setLightboxUrl(null)}
            aria-label="Fechar"
            className="absolute text-white p-2 cursor-pointer"
            style={{ top: "1rem", right: "1rem" }}
          >
            <Dismiss24Regular className="w-6 h-6" />
          </button>
          <img
            src={lightboxUrl}
            alt="Pré-visualização"
            className="object-contain"
            style={{ maxWidth: "90vw", maxHeight: "90vh" }}
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
